import React from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const GridView = ({ projects, onProjectSelect }) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {projects?.map((project, index) => (
        <motion.div
          key={project?.id}
          className="group cursor-pointer"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: index * 0.05 }}
          onClick={() => onProjectSelect(project)}
        >
          <div className="glass-panel rounded-xl overflow-hidden hover:shadow-cosmic-lg transition-all duration-300 cosmic-glow-hover">
            {/* Image */}
            <div className="relative aspect-video overflow-hidden">
              <Image
                src={project?.image}
                alt={project?.title}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
              />
              
              {/* Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              
              {/* Category Badge */}
              <div className="absolute top-3 left-3">
                <div className={`px-2 py-1 rounded-full text-xs font-accent font-medium backdrop-blur-sm border ${
                  project?.category === 'Web Development' ?'bg-accent/20 text-accent border-accent/30'
                    : project?.category === 'Creative Coding' ?'bg-cosmic-energy/20 text-cosmic-energy border-cosmic-energy/30' :'bg-cosmic-cta/20 text-cosmic-cta border-cosmic-cta/30'
                }`}>
                  {project?.category}
                </div>
              </div>

              {/* Quick Actions */}
              <div className="absolute top-3 right-3 flex space-x-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <Button
                  variant="ghost"
                  size="xs"
                  iconName="ExternalLink"
                  iconSize={14}
                  className="bg-background/80 hover:bg-background/90 text-foreground"
                  onClick={(e) => {
                    e?.stopPropagation();
                    window.open(project?.liveUrl, '_blank');
                  }}
                />
                <Button
                  variant="ghost"
                  size="xs"
                  iconName="Github"
                  iconSize={14}
                  className="bg-background/80 hover:bg-background/90 text-foreground"
                  onClick={(e) => {
                    e?.stopPropagation();
                    window.open(project?.githubUrl, '_blank');
                  }}
                />
              </div>

              {/* Rating */}
              <div className="absolute bottom-3 right-3 flex items-center space-x-1 bg-background/80 backdrop-blur-sm rounded-full px-2 py-1">
                <Icon name="Star" size={12} className="text-warning" />
                <span className="text-xs font-medium text-foreground">{project?.rating}</span>
              </div>
            </div>

            {/* Content */}
            <div className="p-4 space-y-3">
              <div>
                <h3 className="font-headline font-bold text-lg text-foreground group-hover:text-accent transition-colors line-clamp-1">
                  {project?.title}
                </h3>
                <p className="font-body text-sm text-muted-foreground line-clamp-2 mt-1">
                  {project?.description}
                </p>
              </div>

              {/* Meta Info */}
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <div className="flex items-center space-x-3">
                  <div className="flex items-center space-x-1">
                    <Icon name="Building" size={12} />
                    <span>{project?.client}</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Icon name="Calendar" size={12} />
                    <span>{project?.year}</span>
                  </div>
                </div>
                <div className="flex items-center space-x-1">
                  <Icon name="Clock" size={12} />
                  <span>{project?.duration}</span>
                </div>
              </div>

              {/* Technologies */}
              <div className="flex flex-wrap gap-1">
                {project?.technologies?.slice(0, 4)?.map((tech, idx) => (
                  <span
                    key={idx}
                    className="px-2 py-1 bg-surface/50 text-muted-foreground rounded text-xs border border-border/20"
                  >
                    {tech}
                  </span>
                ))}
                {project?.technologies?.length > 4 && (
                  <span className="px-2 py-1 bg-surface/50 text-accent rounded text-xs border border-accent/20">
                    +{project?.technologies?.length - 4}
                  </span>
                )}
              </div>

              {/* Impact Metric */}
              <div className="pt-2 border-t border-border/20">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Impact:</span>
                  <span className="text-xs font-medium text-accent">{project?.impact}</span>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      ))}
    </div>
  );
};

export default GridView;